// src/cases/FormSimpleList.js
// Admin "FORM SIMPLE" zone: lists the leads captured by the simplified
// calculator form (Firestore collection `formSimpleLeads`).
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Box, CircularProgress, Button, Typography, Select, MenuItem, FormControl, InputLabel } from "@mui/material";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import {
  getFirestore,
  collection,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  onSnapshot,
  getCountFromServer,
  doc,
  updateDoc,
} from "firebase/firestore";
import { formatDateForDisplay } from "../utils/dateUtils";
import { agentOptionsForValue, normalizeAgentValue } from "./cofidisAgentConfig";
import {
  FormSimpleFilter,
  formSimpleDateBounds,
  normalizeFormSimpleFilters,
} from "./FormSimpleFilter";

const COLLECTION = "formSimpleLeads";

const FirstHolderNameField = ({ record }) => {
  const titular = record?.primerTitular || {};
  const nombre = titular.nombre || record?.nombre || "";
  const apellidos = titular.apellidos || record?.apellidos || "";
  const fullName = `${nombre} ${apellidos}`.trim();
  return (
    <span style={{ fontWeight: 500, color: "var(--ed-text, #212529)" }}>{fullName || "-"}</span>
  );
};

const PhoneField = ({ record }) => (
  <span style={{ fontWeight: 500, color: "var(--ed-text, #212529)" }}>
    {record?.primerTitular?.telefono || record?.telefono || "-"}
  </span>
);

// Inline editable Operaciones / Comercial select, writing back to Firestore.
const InlineSelectCell = ({ record, field, recordId }) => {
  const [value, setValue] = useState(normalizeAgentValue(field, record?.[field]));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setValue(normalizeAgentValue(field, record?.[field]));
  }, [field, record]);

  const handleChange = async (event) => {
    const nextValue = normalizeAgentValue(field, event.target.value);
    const previousValue = value;
    setValue(nextValue);
    setSaving(true);
    try {
      const db = getFirestore();
      await updateDoc(doc(db, COLLECTION, recordId), { [field]: nextValue });
    } catch (err) {
      console.error("Error saving selection:", err);
      setValue(previousValue);
    } finally {
      setSaving(false);
    }
  };

  return (
    <select
      value={value}
      onChange={handleChange}
      onClick={(e) => e.stopPropagation()}
      disabled={saving}
      style={{
        width: "160px",
        border: "1px solid var(--ed-border, #ced4da)",
        borderRadius: "6px",
        padding: "6px 28px 6px 10px",
        fontSize: "0.875rem",
        color: "var(--ed-text, #212529)",
        backgroundColor: saving ? "var(--ed-soft, #f8f9fa)" : "var(--ed-card, #ffffff)",
        cursor: saving ? "wait" : "pointer",
        outline: "none",
      }}
    >
      {agentOptionsForValue(field, record?.[field]).map((option) => (
        <option key={option} value={option}>
          {option}
        </option>
      ))}
    </select>
  );
};

const buildShowUrl = (id) => {
  if (typeof window === "undefined" || !id) {
    return `#/${COLLECTION}/${id}/show`;
  }
  const usesHashRouter = window.location.hash.startsWith("#/");
  const path = `/${COLLECTION}/${id}/show`;
  return usesHashRouter
    ? `${window.location.pathname}${window.location.search}#${path}`
    : path;
};

const thStyle = {
  padding: "20px 24px",
  textAlign: "left",
  borderBottom: "2px solid var(--ed-line, #e9ecef)",
  fontWeight: 600,
  fontSize: "0.9rem",
  color: "var(--ed-text-2, #495057)",
  textTransform: "uppercase",
  letterSpacing: "0.5px",
};

const tdStyle = {
  padding: "20px 24px",
  borderBottom: "1px solid var(--ed-line, #e9ecef)",
  fontWeight: 500,
  fontSize: "0.9rem",
  color: "var(--ed-text, #212529)",
};

export const FormSimpleList = () => {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(100);
  const [total, setTotal] = useState(0);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [filters, setFilters] = useState(normalizeFormSimpleFilters());

  const paginationCursors = useRef({ 1: null });
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const fetchLeads = useCallback((targetPage, pageSize, rawFilters) => {
    setLoading(true);
    const db = getFirestore();
    const ref = collection(db, COLLECTION);
    const currentFilters = normalizeFormSimpleFilters(rawFilters);
    const dateBounds = formSimpleDateBounds(currentFilters);
    const constraints = [];

    if (currentFilters.operacionesAgent) {
      constraints.push(where("operacionesAgent", "==", currentFilters.operacionesAgent));
    }
    if (currentFilters.comercialAgent) {
      constraints.push(where("comercialAgent", "==", currentFilters.comercialAgent));
    }
    if (dateBounds.from) constraints.push(where("registeredDate", ">=", dateBounds.from));
    if (dateBounds.toExclusive) constraints.push(where("registeredDate", "<", dateBounds.toExclusive));
    constraints.push(orderBy("registeredDate", "desc"));

    let q;
    if (targetPage === 1) {
      q = query(ref, ...constraints, limit(pageSize + 1));
      paginationCursors.current = { 1: null };
    } else {
      const cursor = paginationCursors.current[targetPage];
      if (!cursor) {
        if (isMountedRef.current) {
          setLeads([]);
          setHasNextPage(false);
          setLoading(false);
        }
        return () => {};
      }
      q = query(ref, ...constraints, startAfter(cursor), limit(pageSize + 1));
    }

    return onSnapshot(q, (snapshot) => {
      if (!isMountedRef.current) return;
      const docs = snapshot.docs;
      const hasMore = docs.length > pageSize;
      const docsToShow = hasMore ? docs.slice(0, pageSize) : docs;

      if (hasMore && docsToShow.length > 0) {
        paginationCursors.current[targetPage + 1] = docsToShow[docsToShow.length - 1];
      }

      setLeads(docsToShow.map((d) => ({ id: d.id, ...d.data() })));
      setHasNextPage(hasMore);
      setLoading(false);
    }, (error) => {
      console.error("Error fetching form simple leads:", error);
      if (isMountedRef.current) {
        setLeads([]);
        setHasNextPage(false);
        setLoading(false);
      }
    });
  }, []);

  const fetchTotalCount = useCallback(async (rawFilters) => {
    try {
      const db = getFirestore();
      const currentFilters = normalizeFormSimpleFilters(rawFilters);
      const dateBounds = formSimpleDateBounds(currentFilters);
      const constraints = [];
      if (currentFilters.operacionesAgent) {
        constraints.push(where("operacionesAgent", "==", currentFilters.operacionesAgent));
      }
      if (currentFilters.comercialAgent) {
        constraints.push(where("comercialAgent", "==", currentFilters.comercialAgent));
      }
      if (dateBounds.from) constraints.push(where("registeredDate", ">=", dateBounds.from));
      if (dateBounds.toExclusive) constraints.push(where("registeredDate", "<", dateBounds.toExclusive));
      const countQuery = constraints.length
        ? query(collection(db, COLLECTION), ...constraints)
        : collection(db, COLLECTION);
      const snapshot = await getCountFromServer(countQuery);
      if (isMountedRef.current) setTotal(snapshot.data().count);
    } catch (error) {
      console.warn("Error fetching total count:", error);
    }
  }, []);

  useEffect(() => {
    const unsubscribe = fetchLeads(page, perPage, filters);
    return unsubscribe;
  }, [page, perPage, filters, fetchLeads]);

  useEffect(() => {
    fetchTotalCount(filters);
  }, [filters, fetchTotalCount]);

  const handleFiltersChange = (nextFilters) => {
    paginationCursors.current = { 1: null };
    setPage(1);
    setFilters(normalizeFormSimpleFilters(nextFilters));
  };

  const handlePerPageChange = (event) => {
    setPerPage(parseInt(event.target.value, 10));
    setPage(1);
    paginationCursors.current = { 1: null };
  };

  const totalPages = Math.max(1, Math.ceil(total / perPage));

  return (
    <div
      className="MuiPaper-root MuiCard-root"
      style={{
        borderRadius: "12px",
        boxShadow: "0 4px 20px rgba(0, 0, 0, 0.08)",
        overflow: "hidden",
        backgroundColor: "var(--ed-card, #ffffff)",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          padding: "20px 24px",
          justifyContent: "space-between",
          backgroundColor: "var(--ed-soft, #f8f9fa)",
          borderBottom: "1px solid var(--ed-line, #e9ecef)",
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600, color: "var(--ed-text, #212529)" }}>
          Form Simple
        </Typography>
        <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
          <FormSimpleFilter filters={filters} setFilters={handleFiltersChange} />
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Per Page</InputLabel>
            <Select value={perPage} label="Per Page" onChange={handlePerPageChange}>
              <MenuItem value={10}>10</MenuItem>
              <MenuItem value={25}>25</MenuItem>
              <MenuItem value={50}>50</MenuItem>
              <MenuItem value={100}>100</MenuItem>
            </Select>
          </FormControl>
          <Typography variant="body2" color="textSecondary">
            {total > 0
              ? `Showing ${(page - 1) * perPage + 1}-${Math.min(page * perPage, total)} of ${total}`
              : "No results"}
          </Typography>
        </div>
      </div>

      <div style={{ padding: "24px" }}>
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" p={3}>
            <CircularProgress />
          </Box>
        ) : leads.length === 0 ? (
          <Box display="flex" justifyContent="center" alignItems="center" p={3}>
            <div>No hay registros del formulario simplificado todavía.</div>
          </Box>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                background: "var(--ed-card, #ffffff)",
                fontSize: "0.875rem",
                boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
                borderRadius: "8px",
                overflow: "hidden",
              }}
            >
              <thead>
                <tr style={{ height: "56px", backgroundColor: "var(--ed-soft, #f8f9fa)" }}>
                  <th style={thStyle}>Primer Titular</th>
                  <th style={thStyle}>Segundo Titular</th>
                  <th style={thStyle}>Telefono</th>
                  <th style={thStyle}>Fecha de Registro</th>
                  <th style={thStyle}>Operaciones</th>
                  <th style={thStyle}>Comercial</th>
                  <th style={{ ...thStyle, textAlign: "center" }}>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {leads.map((record, index) => (
                  <tr
                    key={record.id}
                    style={{
                      height: "64px",
                      backgroundColor: index % 2 === 0 ? "var(--ed-card, #ffffff)" : "var(--ed-soft, #f8f9fa)",
                    }}
                  >
                    <td style={tdStyle}>
                      <FirstHolderNameField record={record} />
                    </td>
                    <td style={tdStyle}>
                      <span style={{ color: "var(--ed-text-3, #6c757d)", fontStyle: "italic" }}>-</span>
                    </td>
                    <td style={tdStyle}>
                      <PhoneField record={record} />
                    </td>
                    <td style={tdStyle}>{formatDateForDisplay(record.registeredDate)}</td>
                    <td style={tdStyle}>
                      <InlineSelectCell record={record} field="operacionesAgent" recordId={record.id} />
                    </td>
                    <td style={tdStyle}>
                      <InlineSelectCell record={record} field="comercialAgent" recordId={record.id} />
                    </td>
                    <td style={{ ...tdStyle, textAlign: "center" }}>
                      <Button
                        size="small"
                        variant="outlined"
                        color="primary"
                        startIcon={<OpenInNewIcon fontSize="small" />}
                        href={buildShowUrl(record.id)}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                      >
                        Abrir
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {total > 0 && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            padding: "20px 24px",
            borderTop: "1px solid var(--ed-line, #e9ecef)",
            gap: 2,
          }}
        >
          <Button
            variant="outlined"
            disabled={page <= 1 || loading}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
          >
            Previous
          </Button>
          <Typography variant="body2" color="textSecondary">
            Page {page} of {totalPages}
          </Typography>
          <Button
            variant="outlined"
            disabled={!hasNextPage || loading}
            onClick={() => setPage((p) => p + 1)}
          >
            Next
          </Button>
        </Box>
      )}
    </div>
  );
};
